import { Button } from '@/app/common/components/button/Button'
import { Input } from '@/app/common/components/input/Input'
import Label from '@/app/common/components/Label'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { EditIconGeneral } from '@/app/common/icons/general'
import { parseRequestError } from '@/app/common/utilities'
import { usePutProgramCatalogCategory } from '@/queries/program_catalog/hooks/usePutProgramCatalogCategory'
import { ProgramCategory } from '@/queries/program_catalog/schema'
import { Checkbox } from '@/shad/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shad/ui/dialog'
import { useToast } from '@/shad/ui/toast/use-toast'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { z } from 'zod'
interface Props {
  category: ProgramCategory
  onEditSuccess: () => void
}

// Define Zod schema for form validation
const formSchema = z.object({
  serviceFee: z
    .string()
    .min(1, { message: 'Service fee is required' })
    .refine((val) => !isNaN(parseFloat(val)), {
      message: 'Service fee must be a valid number',
    })
    .refine((val) => parseFloat(val) >= 0, {
      message: 'Service fee must be non-negative',
    }),
  apply_service_fee_to_items: z.boolean().optional(),
})

type FormValues = z.infer<typeof formSchema>

function EditCategoryPopup(props: Props) {
  const { category, onEditSuccess } = props
  const programId = useSelector(selectSelectedProgram).id

  // Dialog state
  const [isOpen, setIsOpen] = useState(false)
  const { toast } = useToast()

  const editCategoryMutation = usePutProgramCatalogCategory({
    onSuccess: () => {
      toast({
        title: 'Category Updated',
        description: `The category has been updated successfully.`,
        variant: 'default',
      })
      setIsOpen(false)
      onEditSuccess()
    },
    onError: (error) => {
      toast({
        title: 'Error Updating Category',
        description: parseRequestError(error).message,
        variant: 'destructive',
      })
    },
  })

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      apply_service_fee_to_items: category.apply_service_fee_to_items === 1,
      serviceFee: category.service_fee.toString(),
    },
  })

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
  }

  const onSubmit = (values: FormValues) => {
    const serviceFee = parseFloat(values.serviceFee)
    const shouldApplyServiceFee = values.apply_service_fee_to_items

    editCategoryMutation.mutate({
      apply_service_fee_to_items: shouldApplyServiceFee ? 1 : 0,
      service_fee: serviceFee,
      categoryId: category.id,
      programId: programId,
    })
  }

  // Custom handler for service fee input to only allow numeric values
  const handleServiceFeeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (value === '' || /^(\d+\.?\d*|\.\d+)$/.test(value)) {
      e.target.value = value
    } else {
      e.preventDefault()
    }
  }

  // Otherwise, show the edit button and dialog
  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <button
          className="flex items-center gap-2 hover:bg-gray-100 w-full py-2 px-2 rounded-md"
          onClick={() => setIsOpen(true)}
        >
          <EditIconGeneral /> Edit
        </button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Category</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-4">
          <div className="space-y-1">
            <Label htmlFor="serviceFee">
              Service Fee (%)<span className="text-red-500">*</span>
            </Label>
            <Input
              id="serviceFee"
              {...register('serviceFee')}
              placeholder="Enter service fee percentage"
              className={errors.serviceFee ? 'border-red-500' : ''}
              onChange={handleServiceFeeChange}
            />
            {errors.serviceFee && (
              <p className="text-red-500 text-sm">
                {errors.serviceFee.message}
              </p>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <Controller
              control={control}
              name="apply_service_fee_to_items"
              render={({ field }) => {
                return (
                  <Checkbox
                    id="apply_service_fee_to_items"
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      field.onChange(checked)
                    }}
                  />
                )
              }}
            />

            <Label htmlFor="apply_service_fee_to_items">
              Apply service fee to all items in this category
            </Label>
          </div>

          <DialogFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <div>
              <SubmitButton
                buttonTxt="Save changes"
                status={editCategoryMutation.status}
              />
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default EditCategoryPopup
